export const getOfflineUsageBarProps = (drmValuesContext) => {
  const { offlinePercentMaximum, offlinePercentUsed, offlinePercentRemaining } =
    drmValuesContext;

  const offlineUsageBarProps = {
    accordionLabel: 'Download Usage',
    alert: {
      menu: 'download',
    },
    defaultExpanded: true,
    hasAccordion: true,
    usageText: 'Currently Downloaded',
    availablePercent: offlinePercentMaximum,
    currentPercent: offlinePercentUsed,
    remainingAllowance: offlinePercentRemaining,
  };

  return offlineUsageBarProps;
};

export const getOfflineUnavailableMessageProps = (
  offlineSession,
  offlineSessions,
  drmValuesContext,
  topLevelMarkers,
  isOffline,
) => {
  const {
    offlineEnabled,
    offlinePercentMaximum,
    offlinePercentUsed,
    offlinePercentRemaining,
    offlineCountRemaining,
  } = drmValuesContext;

  const deviceInfoDescriptions =
    offlineSessions?.map(
      (offlineSession) => offlineSession?.device?.description ?? '',
    ) ?? [];

  // offlineAvailable- true if offlineEnabled for TOC books and if 100% downloadable for noTOC books
  const offlineAvailable = Boolean(topLevelMarkers.length)
    ? offlineEnabled
    : offlineEnabled && offlinePercentMaximum === 1;

  // hasOfflinePercentSet- true if there is a percentage remaining and a max limit.
  const hasOfflinePercentSet =
    Boolean(offlinePercentRemaining) && Boolean(offlinePercentMaximum);

  // offlineLimitReached- true if no Offline count is remaining and if user already has no existing session on the device.
  const offlineLimitReached =
    !Boolean(offlineCountRemaining) && !Boolean(offlineSession);

  const shouldShowOfflineUnavailableMessage =
    isOffline || // offline
    offlineLimitReached || // user cannot have offline sessions
    !offlineAvailable || // book not offlinable
    (offlineAvailable && !hasOfflinePercentSet && offlinePercentUsed === 0); // book is offline enabled but no percentage is set and user has not been able to download any offline content.

  if (shouldShowOfflineUnavailableMessage) {
    return {
      deviceInfoDescriptions,
      isOffline,
      offlineLimitReached,
      offlineAvailable,
    };
  }

  return shouldShowOfflineUnavailableMessage;
};

const getFocusPrevOrNextSibling = (child) => {
  return child.previousElementSibling
    ? child.previousElementSibling
    : child.nextElementSibling;
};

const getShouldFocusFirstItem = (child) => {
  return child.previousElementSibling ? false : true;
};

const getItem = (itemsRef, index) =>
  itemsRef.current.querySelector(`#offline-listitem-${index}`);

export const getChildToFocus = (marker, itemsRef, markersDownloaded) => {
  // the selected marker
  const activeChild = getItem(itemsRef, marker.index);

  if (marker.isTopLevelMarker) {
    const childToFocus = getFocusPrevOrNextSibling(activeChild);
    const shouldFocusFirstItem = getShouldFocusFirstItem(activeChild);
    return { childToFocus, shouldFocusFirstItem };
  }

  const parentMarkerForChild = marker.topLevelMarker;
  // if this isn't a top level marker we don't want the focus behaving as usual
  const areMoreSiblingsDownloaded = parentMarkerForChild.descendents.some(
    (c) =>
      c !== marker &&
      !marker.descendents.includes(c) &&
      markersDownloaded.has(c),
  );

  if (areMoreSiblingsDownloaded) {
    const childToFocus = activeChild;
    return { childToFocus, shouldFocusFirstItem: true };
  }

  // if this is the last child of the parent marker downloaded, we focus the prev parent
  const parentMarker = getItem(itemsRef, parentMarkerForChild.index);
  const childToFocus = getFocusPrevOrNextSibling(parentMarker);
  const shouldFocusFirstItem = getShouldFocusFirstItem(parentMarker);
  return { childToFocus, shouldFocusFirstItem };
};

export const getAvailableForDownloadChildToFocus = (
  marker,
  contentToDownloadItemsRef,
  availableContent,
  markersDownloaded,
) => {
  const currentTopLevelMarkerIndex = availableContent.findIndex(
    (aMarker) => aMarker === marker.topLevelMarker,
  );

  let firstAvailableMarker;
  for (let i = currentTopLevelMarkerIndex; i >= 0; i--) {
    if (
      [...markersDownloaded].every(
        (marker) => marker.topLevelMarker !== availableContent[i],
      )
    ) {
      firstAvailableMarker = availableContent[i];
      break;
    }
  }

  if (firstAvailableMarker) {
    const activeChild = getItem(
      contentToDownloadItemsRef,
      firstAvailableMarker.index,
    );
    return { childToFocus: activeChild, shouldFocusFirstItem: false };
  }

  if (!firstAvailableMarker) {
    for (
      let i = currentTopLevelMarkerIndex;
      i <= availableContent.length;
      i++
    ) {
      if (
        [...markersDownloaded].every(
          (marker) => marker.topLevelMarker !== availableContent[i],
        )
      ) {
        firstAvailableMarker = availableContent[i];
        break;
      }
    }
  }

  if (firstAvailableMarker) {
    const activeChild = getItem(
      contentToDownloadItemsRef,
      firstAvailableMarker.index,
    );

    return { childToFocus: activeChild, shouldFocusFirstItem: true };
  } else {
    // there are no available markers to download left
    return { childToFocus: null };
  }
};

export const focusButton = (childToFocus, shouldFocusFirstItem) => {
  const statusButtons = childToFocus.querySelectorAll(
    '[data-status="offline-status-button"]',
  );

  const statusButton = shouldFocusFirstItem
    ? statusButtons[0]
    : statusButtons[statusButtons.length - 1];
  statusButton.focus();

  return statusButton;
};
